import { useState } from "react";
import { motion } from "framer-motion";
import { BadgeCheck } from "lucide-react";


const fmtPrice = (n) =>
  n == null
    ? "—"
    : n >= 1000
      ? n.toLocaleString("en-US", { maximumFractionDigits: 0 })
      : n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function Logo({ stock }) {
  const [failed, setFailed] = useState(false);
  if (failed) {
    return (
      <span className="flex h-8 w-8 items-center justify-center border border-gold/30 bg-gold/[0.06] font-tabular text-[10px] text-gold">
        {stock.symbol.slice(0, 2)}
      </span>
    );
  }
  return (
    <img
      src={stock.logo}
      alt={`${stock.name} logo`}
      loading="lazy"
      onError={() => setFailed(true)}
      className="h-8 w-8 rounded-sm border border-white/10 bg-white/95 object-contain p-1"
    />
  );
}

export default function StocksBoard({ rows }) {
  if (!rows || rows.length === 0) return null;
  return (
    <section data-testid="stocks-board" className="mx-auto max-w-7xl px-6 pb-24 lg:px-10 lg:pb-32">
      <motion.div
        initial={{ opacity: 0, y: 32 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
        className="mb-12 flex flex-col gap-6 md:flex-row md:items-end md:justify-between"
      >
        <div>
          <p className="mb-4 text-[10px] uppercase tracking-[0.3em] text-gold">
            Equities · Live
          </p>
          <h2 className="font-serif text-5xl font-light tracking-tighter text-white sm:text-6xl">
            Wall Street,
            <span className="italic text-gold"> verified.</span>
          </h2>
        </div>
        <p className="max-w-xs text-sm leading-relaxed text-white/50">
          Twenty-five blue-chip names, quoted in real time beside the digital
          book — every listing verified on the Aureon board.
        </p>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 32 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
        className="grid grid-cols-1 gap-x-12 border border-white/10 bg-[#141414]/40 px-6 py-2 backdrop-blur-xl sm:grid-cols-2"
      >
        {rows.map((s, i) => {
          const chg = s.change_pct;
          const up = (chg ?? 0) > 0;
          const flat = chg === 0;
          return (
            <motion.a
              key={s.symbol}
              href="/markets?tab=stocks"
              data-testid={`stock-row-${s.symbol.toLowerCase()}`}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-20px" }}
              transition={{ duration: 0.5, delay: Math.min(i, 10) * 0.04 }}
              className="group flex items-center justify-between gap-4 border-t border-white/10 py-3.5 transition-colors duration-300 first:border-t-0 sm:[&:nth-child(2)]:border-t-0 hover:bg-white/[0.02]"
            >
              <span className="flex items-center gap-3 overflow-hidden">
                <Logo stock={s} />
                <span className="flex items-baseline gap-2 overflow-hidden">
                  <span className="text-sm text-white transition-colors duration-300 group-hover:text-gold">
                    {s.symbol}
                  </span>
                  <span className="hidden truncate text-[10px] text-white/55 md:inline">{s.name}</span>
                  {s.verified && (
                    <BadgeCheck
                      className="h-3.5 w-3.5 shrink-0 text-gold"
                      data-testid={`stock-verified-${s.symbol.toLowerCase()}`}
                      aria-label="Verified listing"
                    />
                  )}
                </span>
              </span>
              <span className="flex items-baseline gap-2.5">
                <span className="font-tabular text-sm text-white/85" data-testid={`stock-price-${s.symbol.toLowerCase()}`}>
                  ${fmtPrice(s.price)}
                </span>
                {chg != null && (
                  <span
                    className={`font-tabular text-[10px] ${flat ? "text-white/40" : up ? "text-[#00FF41]" : "text-[#FF003C]"}`}
                    data-testid={`stock-change-${s.symbol.toLowerCase()}`}
                  >
                    {flat ? "•" : up ? "▲" : "▼"} {Math.abs(chg).toFixed(2)}%
                  </span>
                )}
              </span>
            </motion.a>
          );
        })}
      </motion.div>
    </section>
  );
}
